import React, { useState, useEffect } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { CustomTable } from "../../components/Table";
import { POST } from "../../utils/webService";
import { GET_PLANS_HISTORY } from "../../utils/ApiUrls"; 

interface PlanHistoryData {
  _id: string;
  planName: string;
  planStartDate: number;
  planEndDate: number;
  price: number;
  validity: string;
  preTaxPrice: number;
  taxAmount: number;
}

interface PlanHistoryCardProps {}

const PlanHistoryCard: React.FC<PlanHistoryCardProps> = () => {
  const [planHistoryData, setPlanHistoryData] = useState<PlanHistoryData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const columnsHelper = createColumnHelper<any>();

  // Format date function
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);
    return `${day} ${month} ${year}`;
  };

  // Format subscription period
  const formatSubscriptionPeriod = (validity: string): string => {
    return validity === 'MONTHLY' ? 'Monthly' : 'Yearly';
  };

  // Prepare data for table
  const prepareTableData = () => {
    return planHistoryData.map((plan) => ({
      planName: plan.planName,
      purchaseDate: formatDate(plan.planStartDate),
      expiryDate: formatDate(plan.planEndDate),
      subscriptionAmount: `₹${plan.price}`,
      subscriptionPeriod: formatSubscriptionPeriod(plan.validity)
    }));
  };

  // Table columns configuration
  const planHistoryColumns = [
    columnsHelper.accessor("planName", {
      header: () => (
        <div className="text-left">
          <p className="text-xs font-Open font-normal text-[#777777] mb-1">Plan Name</p>
        </div>
      ),
      cell: (info: any) => (
        <div className="text-left">
          <p className="font-Open text-sm font-semibold text-[#1C1C1C]">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnsHelper.accessor("purchaseDate", {
      header: () => (
        <div className="text-left">
          <p className="text-xs font-Open font-normal text-[#777777] mb-1">Purchase Date</p>
        </div>
      ),
      cell: (info: any) => (
        <div className="text-left">
          <p className="font-Open text-sm font-normal text-[#1C1C1C]">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnsHelper.accessor("expiryDate", {
      header: () => (
        <div className="text-left">
          <p className="text-xs font-Open font-normal text-[#777777] mb-1">Expiry Date</p>
        </div>
      ),
      cell: (info: any) => (
        <div className="text-left">
          <p className="font-Open text-sm font-normal text-[#1C1C1C]">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnsHelper.accessor("subscriptionAmount", {
      header: () => (
        <div className="text-left">
          <p className="text-xs font-Open font-normal text-[#777777] mb-1">Subscription Amount</p>
        </div>
      ),
      cell: (info: any) => (
        <div className="text-left">
          <p className="font-Open text-sm font-semibold text-[#1C1C1C]">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
    columnsHelper.accessor("subscriptionPeriod", {
      header: () => (
        <div className="text-left">
          <p className="text-xs font-Open font-normal text-[#777777] mb-1">Subscription Period</p>
        </div>
      ),
      cell: (info: any) => (
        <div className="text-left">
          <p className="font-Open text-sm font-normal text-[#1C1C1C]">
            {info.getValue()}
          </p>
        </div>
      ),
    }),
  ];

  // Fetch plan history data
  const fetchPlanHistory = async () => {
    setIsLoading(true);
    setError("");
    
    try {
      const { data }: any = await POST(GET_PLANS_HISTORY);
      
      if (data?.success && data?.data) {
        setPlanHistoryData(data.data);
      } else {
        setError(data?.message || "Failed to fetch plan history");
      }
    } catch (error) {
      console.error("Error fetching plan history:", error);
      setError("An error occurred while fetching plan history");
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when component expands
  useEffect(() => {
    if (isExpanded && planHistoryData.length === 0) {
      fetchPlanHistory();
    }
  }, [isExpanded]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="bg-white rounded-lg border border-[#E8E8E8] overflow-hidden shadow-md">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={toggleExpand}
      >
        <div className="flex items-center gap-2">
          <h2 className="font-Open text-base font-semibold text-[#1C1C1C]">
            Plan History
          </h2>
          {planHistoryData.length > 0 && (
            <span className="text-sm font-Open font-normal text-[#777777]">
              ({planHistoryData.length} Previous Plans)
            </span>
          )}
        </div>
        <div className="transition-transform duration-200">
          {isExpanded ? (
            <svg className="w-5 h-5 text-[#777777]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m18 15-6-6-6 6" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-[#777777]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m6 9 6 6 6-6" />
            </svg>
          )}
        </div>
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <div className="border-t border-[#E8E8E8]">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#004EFF]"></div>
              <p className="mt-2 text-sm font-Open text-[#777777]">Loading plan history...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <p className="text-sm font-Open text-red-500">{error}</p>
              <button 
                onClick={fetchPlanHistory}
                className="mt-2 px-4 py-2 bg-[#004EFF] text-white text-sm font-Open rounded hover:bg-blue-600 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : planHistoryData.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm font-Open text-[#777777]">No plan history available</p>
            </div>
          ) : (
            <div className="p-4">
              <CustomTable
                rowData={prepareTableData()}
                columnsData={planHistoryColumns}
                theadClassName="bg-white border-b border-[#E8E8E8]"
                rowCellClassName="py-3 px-0 border-b border-[#F5F5F5]"
                rowClassName="hover:bg-gray-50"
                minHeight="300px"
                currentPage={1}
                pageSize={10}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlanHistoryCard;